import { schema } from '@mesa/core/zero';
import { EyesIcon } from '@phosphor-icons/react';
import { Row } from '@rocicorp/zero';
import { useParams } from '@tanstack/react-router';
import { GitMergeIcon, GitPullRequestClosedIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { ReviewForm, type ReviewEvent } from './review-form';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SplitButton, SplitButtonPrimary, SplitButtonSecondary, SplitButtonItem } from '@/components/ui/split-button';
import { useMergeStrategies } from '@/features/pulls/hooks/use-merge-strategies';
import { MergeStrategy } from '@/features/pulls/services/pulls';
import { cn } from '@/lib/utils';
import { useZero } from '@/lib/zero';

type PullRecordRow = Row<typeof schema.tables.PullsTable>;

interface ReviewButtonProps {
  alreadyReviewed: boolean;
  pull: PullRecordRow;
  openMergeDialog: (mergeStrategy: MergeStrategy) => void;
  hasConflicts: boolean | undefined;
}

export function ReviewButton({ alreadyReviewed, pull, openMergeDialog, hasConflicts }: ReviewButtonProps) {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const z = useZero();
  const { defaultStrategy } = useMergeStrategies();

  const [reviewSubmission, setReviewSubmission] = useState<{
    body: string;
    event: ReviewEvent;
  }>({
    body: '',
    event: 'APPROVE',
  });

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  const handleMerge = useCallback(() => {
    openMergeDialog(defaultStrategy);
  }, [openMergeDialog, defaultStrategy]);

  const handleClose = useCallback(() => {
    if (!pull) return;
    z.mutate.PullsTable.openOrClose({
      nodeId: pull.nodeId,
      owner: pull.owner,
      repo: pull.repo,
      number: pull.number,
      event: 'CLOSE',
    });
  }, [z, pull]);

  const handleSubmitReview = useCallback(async () => {
    setIsSubmittingReview(true);
    try {
      await z.mutate.PullsTable.submitReview({
        nodeId: pull.nodeId,
        owner,
        repo,
        number: Number(number),
        body: reviewSubmission.body,
        event: reviewSubmission.event,
      }).server;

      setIsPopoverOpen(false);
      setReviewSubmission({
        body: '',
        event: 'APPROVE',
      });
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setIsSubmittingReview(false);
    }
  }, [z, pull, owner, repo, number, reviewSubmission]);

  return (
    <SplitButton variant="outline" className={cn(!alreadyReviewed && 'border border-blue-600 rounded-md')}>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <SplitButtonPrimary>
            <EyesIcon className="size-4" />
            {alreadyReviewed ? 'Re-review' : <span className="text-blue-600">Review</span>}
          </SplitButtonPrimary>
        </PopoverTrigger>
        <PopoverContent className="w-82 p-0 overflow-hidden">
          <ReviewForm
            reviewEvent={reviewSubmission.event}
            onEventChange={(event) => setReviewSubmission({ ...reviewSubmission, event })}
            onBodyChange={(body) => setReviewSubmission({ ...reviewSubmission, body })}
            onSubmit={handleSubmitReview}
            isSubmitting={isSubmittingReview}
          />
        </PopoverContent>
      </Popover>

      <SplitButtonSecondary>
        <SplitButtonItem onClick={handleMerge} disabled={hasConflicts === true || hasConflicts === undefined}>
          <GitMergeIcon className="size-4 text-indigo-600" />
          {hasConflicts === true
            ? 'Merge (Conflicts found)'
            : hasConflicts === undefined
              ? 'Merge (Checking for conflicts...)'
              : 'Merge'}
        </SplitButtonItem>
        <SplitButtonItem onClick={handleClose}>
          <GitPullRequestClosedIcon className="size-4 text-red-500" />
          Close
        </SplitButtonItem>
      </SplitButtonSecondary>
    </SplitButton>
  );
}
